import React from 'react';
import { DisplayText, ToggleButton } from '@superops/ui-design-system';
import { FormattedMessage } from 'react-intl';
import { toast } from '@nile/components';

interface Feature {
  featureId: string;
  name: string;
  description: string;
  state?: string;
  enabled?: boolean;
}

interface RenderFeaturesListProps {
  features: Feature[];
  onToggleFeature: (featureId: string, currentState: string) => void;
  useSubFeatures?: boolean;
}

export const renderFeaturesList = ({
  features,
  onToggleFeature,
  useSubFeatures = false
}: RenderFeaturesListProps) => {
  return features?.map((feature) => (
    <div key={feature.featureId} className="mb-10">
      <div className="flex g10">
        <ToggleButton
          size="small"
          onChange={() =>
            onToggleFeature(feature.featureId, feature.state || 'ACTIVE')
          }
          value={feature.state === 'ACTIVE' || !feature.state}
        />
        <div className="ml-20">
          <div className="">
            <DisplayText size="large" weight="Bold">
              {useSubFeatures ? (
                <FormattedMessage id={feature?.name || 'NA'} />
              ) : (
                feature?.name || 'NA'
              )}
            </DisplayText>
          </div>
          <div className="mt-5">
            <DisplayText variant="subdued">
              {useSubFeatures ? (
                <FormattedMessage id={feature?.description || 'NA'} />
              ) : (
                feature?.description || 'NA'
              )}
            </DisplayText>
          </div>
        </div>
      </div>
    </div>
  ));
};

interface HandleToggleFeatureProps {
  featureId: string;
  currentState: string;
  updateFunction: (params: any) => Promise<void>;
  setFeatures: React.Dispatch<React.SetStateAction<Feature[]>>;
  showToastOnError?: boolean;
}

export const handleToggleFeature = async ({
  featureId,
  currentState,
  updateFunction,
  setFeatures,
  showToastOnError = false
}: HandleToggleFeatureProps) => {
  try {
    const newState = currentState === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

    await updateFunction({
      input: {
        featureId: featureId,
        state: newState
      }
    });

    // Update local state
    setFeatures((prevFeatures) =>
      prevFeatures.map((feature) =>
        feature.featureId === featureId
          ? { ...feature, state: newState, enabled: newState === 'ACTIVE' }
          : feature
      )
    );
  } catch (error) {
    console.error('Error updating feature:', error);
    if (showToastOnError) {
      toast('error', 'Failed to update feature');
    }
  }
};
